// This is the User Management page
// MSP admins can add, remove and change roles of users in their organization

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar"

const API_URL = config.API_URL;

export default function UserManagement() {
  const navigate = useNavigate();

  // Get logged in user info
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  // State variables
  const [users, setUsers] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  // New user form state
  const [newUser, setNewUser] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "viewer"
  });

  function fetchUsers() {
    // Fetches all users for this organization
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/users`)
      .then((res) => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load users");
        setLoading(false);
      });
  }

  function fetchAccessRequests() {
    // Fetches all pending access requests for this organization
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/access-requests`)
      .then((res) => setAccessRequests(res.data))
      .catch(() => setAccessRequests([]));
  }

  useEffect(() => {
    fetchUsers();
    fetchAccessRequests();
  }, [ORG_ID]);

  async function handleApprove(requestId) {
    try {
      await axios.post(`${API_URL}/organizations/${ORG_ID}/access-requests/${requestId}/approve`);
      setFormSuccess("Invite email sent successfully!");
      fetchAccessRequests();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to approve request");
    }
  }

  async function handleDeny(requestId, email) {
    if (!window.confirm(`Deny access request from ${email}?`)) return;
    try {
      await axios.post(`${API_URL}/organizations/${ORG_ID}/access-requests/${requestId}/deny`);
      fetchAccessRequests();
    } catch (err) {
      setError("Failed to deny request");
    }
  }

  async function handleAddUser(e) {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    try {
      await axios.post(
        `${API_URL}/organizations/${ORG_ID}/users?email=${encodeURIComponent(newUser.email)}&password=${encodeURIComponent(newUser.password)}&full_name=${encodeURIComponent(newUser.full_name)}&role=${newUser.role}`,
        null,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );

      setFormSuccess(`User ${newUser.full_name} added successfully`);
      setNewUser({ full_name: "", email: "", password: "", role: "viewer" });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to add user");
    }
  }

  async function handleDeleteUser(userId, userEmail) {
    if (!window.confirm(`Delete user ${userEmail}? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API_URL}/organizations/${ORG_ID}/users/${userId}`, {
  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
});
      fetchUsers();
    } catch (err) {
      setError("Failed to delete user");
    }
  }

  async function handleRoleChange(userId, newRole) {
    try {
      await axios.put(
        `${API_URL}/organizations/${ORG_ID}/users/${userId}/role?role=${newRole}`,
        null,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      fetchUsers();
    } catch (err) {
      setError("Failed to update role");
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading users...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <Navbar />

      {/* Main content */}
      <main className="px-8 py-8">

        {/* Page title and Add User button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">User Management</h1>
            <p className="text-gray-400 mt-1">Manage who has access to your DriverWatch portal</p>
          </div>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setFormError(null);
              setFormSuccess(null);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            {showAddForm ? "Cancel" : "+ Add User"}
          </button>
        </div>

        {/* Success message */}
        {formSuccess && (
          <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {formSuccess}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Add User form */}
        {showAddForm && (
          <div className="bg-gray-900 rounded-xl p-6 border border-blue-800 mb-8">
            <h2 className="text-white font-semibold mb-4">Add New User</h2>

            {formError && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                {formError}
              </div>
            )}

            <form onSubmit={handleAddUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="john@company.com"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="viewer">Viewer — can view data only</option>
                    <option value="admin">Admin — can manage users and devices</option>
                  </select>
                </div>

              </div>

              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Add User
              </button>
            </form>
          </div>
        )}

        {/* Pending Access Requests */}
        {accessRequests.length > 0 && (
          <div className="bg-gray-900 rounded-xl p-6 border border-yellow-800 mb-8">
            <h2 className="text-white font-semibold mb-4">
              Pending Access Requests ({accessRequests.length})
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left py-3">Name</th>
                  <th className="text-left py-3">Email</th>
                  <th className="text-left py-3">Organization</th>
                  <th className="text-left py-3">Requested</th>
                  <th className="text-left py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accessRequests.map((req) => (
                  <tr key={req.id} className="border-b border-gray-800">
                    <td className="py-3 text-white font-medium">{req.full_name}</td>
                    <td className="py-3 text-gray-400">{req.email}</td>
                    <td className="py-3 text-gray-400">{req.organization_name}</td>
                    <td className="py-3 text-gray-400">
                      {new Date(req.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(req.id)}
                          className="text-green-400 hover:text-green-300 text-xs border border-green-800 hover:border-green-600 px-2 py-1 rounded transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleDeny(req.id, req.email)}
                          className="text-red-400 hover:text-red-300 text-xs border border-red-800 hover:border-red-600 px-2 py-1 rounded transition-colors"
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Users table */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <h2 className="text-white font-semibold mb-4">
            Users ({users.length})
          </h2>

          {users.length === 0 ? (
            <p className="text-gray-400">No users found</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left py-3">Name</th>
                  <th className="text-left py-3">Email</th>
                  <th className="text-left py-3">Role</th>
                  <th className="text-left py-3">Added</th>
                  <th className="text-left py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-800">
                    <td className="py-3 text-white font-medium">{user.full_name}</td>
                    <td className="py-3 text-gray-400">{user.email}</td>
                    <td className="py-3">
                      {/* Role dropdown - allows changing role inline */}
                      {user.email === storedUser.email ? (
                        <span className="bg-blue-900 text-blue-300 text-xs px-2 py-1 rounded-full">
                          {user.role} (you)
                        </span>
                      ) : (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className="bg-gray-800 border border-gray-700 text-white rounded px-2 py-1 text-xs focus:outline-none"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      {/* Can't delete yourself */}
                      {user.email !== storedUser.email && (
                        <button
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="text-red-400 hover:text-red-300 text-xs border border-red-800 hover:border-red-600 px-2 py-1 rounded transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </main>
    </div>
  );
}